import type { ReactNode } from "react";
import { ChevronDown, Plus } from "lucide-react";
import type { BollettaVista, Condominio, Contatore, ContatoreGas, OffertaMenu } from "@/api/preventivo";
import { Esiti } from "./Esiti";
import { esitiConFrase, esitiGasVisibili } from "@/lib/preventivoFormato";
import {
  Altri, DETTAGLIO_NON_LEGGIBILE, EliminaBolletta, EtichettaTipo, FOCUS, Importo, Intestazione, MeseLink, NonLetta, NoteRisultato, OffertaBreve, Pcv,
  Ricarico, RiquadroOfferta, Spread, avvisoGas, differenzaBolletta, eIncompleta, esitiGasDi, notaMotivo, offertaDel, offertaDelContatore,
} from "./parti";
import {
  AGGIUNGI_PAGINA, COL_ALTRI, IN_ANALISI, COL_PCV, COL_RICARICO, COL_SPREAD, OFFERTA_CONDOMINIO, SOLO_QUESTA_FORNITURA, addebitiInPiu,
  leBollette,
} from "./scritte";
import { useModo } from "./modo";
import { SceltaOfferta } from "./SceltaOfferta";

/** Bollette con i numeri in vista; dalla quarta in poi stanno dietro «Le n bollette» (spec 2026-10-23 § 3.2). */
const IN_VISTA = 3;

/** I menù dell'offerta nella scheda di una fornitura (03/11, al posto di «Cambia solo questa»), come nella tabella. */
interface Comandi {
  offerte: OffertaMenu[];
  occupato: boolean;
  /** `null` = «Torna all'offerta del condominio». */
  onScegliContatore: (c: Condominio, k: Contatore, offertaId: string | null) => void;
  /** «Elimina» in fondo alla scheda di una bolletta: la pagina chiede conferma. */
  onElimina: (b: BollettaVista) => void;
  avvisoRiga: (k: Contatore) => ReactNode;
}

interface Props extends Comandi {
  condominio: Condominio;
  /** «Cambia» / «Scegli l'offerta»: apre il pannello del condominio. */
  onCambia: () => void;
  /** «Aggiungi pagina» nel riquadro di una bolletta incompleta fotografata; senza, il pulsante non c'è. */
  onPagina?: (bollettaId: string) => void;
  className?: string;
}

/** Riga da aprire: alta 48 px, tutta toccabile; si apre e si chiude con la tastiera (details/summary nativi). */
function DaAprire({ titolo, children }: { titolo: string; children: ReactNode }) {
  return (
    <details className="group border-t border-border">
      <summary className="flex h-12 cursor-pointer list-none items-center justify-between px-4 text-sm font-medium text-text [&::-webkit-details-marker]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand">
        {titolo}
        <ChevronDown size={18} aria-hidden="true" className="text-muted-foreground transition-transform duration-200 group-open:rotate-180 motion-reduce:transition-none" />
      </summary>
      {children}
    </details>
  );
}

/** Un numero della bolletta. Sotto 640 px etichetta a sinistra e valore a destra (`larga`: il valore va a capo su una
 *  riga sua); dai 640 px i quattro numeri in fila, l'etichetta sopra. */
function Voce({ titolo, children, larga = false }: { titolo: string; children: ReactNode; larga?: boolean }) {
  return (
    <div className={`min-w-0 sm:block ${larga ? "" : "flex items-baseline justify-between gap-3"}`}>
      <dt className={`text-muted-foreground sm:text-xs ${larga ? "" : "shrink-0"}`}>{titolo}</dt>
      <dd className={`font-medium text-text break-words sm:mt-0.5 sm:text-left ${larga ? "mt-0.5" : "min-w-0 text-right"}`}>{children}</dd>
    </div>
  );
}

interface PropsBolletta {
  b: BollettaVista;
  k: Contatore;
  offerta: OffertaMenu | null;
  onPagina?: (bollettaId: string) => void;
  /** I menù dell'offerta della fornitura (sotto la sua ultima bolletta letta); `null` = non qui. */
  menu: ReactNode;
  /** «Elimina» (la prima volta che la bolletta compare); `null` = non qui. */
  elimina: ReactNode;
}

function SchedaBolletta({ b, k, offerta, onPagina, menu, elimina }: PropsBolletta) {
  const diff = differenzaBolletta(b, offerta);
  const { solaLettura } = useModo();
  // incompleta: i numeri letti come le altre, senza differenza; il motivo e «Aggiungi pagina» in un riquadro grigio
  const incompleta = eIncompleta(b);
  const nota = notaMotivo(b);
  const pagina = incompleta && b.foto && onPagina && !solaLettura ? onPagina : null;
  const testa = (
    // sotto 640 px la fornitura su una riga sua (accanto c'è la differenza); dai 640 px «mese · fornitura»
    <p className="flex flex-col text-sm sm:flex-row sm:items-center sm:gap-x-1.5">
      <MeseLink b={b} className="self-start" />
      <span className="min-w-0 break-words text-text"><span aria-hidden="true" className="hidden text-muted-foreground sm:inline">·{" "}</span>{k.fornitura}</span>
    </p>
  );
  if (b.stato === "non_letta" && !incompleta) {
    return (
      <li className="px-4 py-2">
        {testa}
        <NonLetta className="text-sm" />
        {elimina && <div className="mt-1">{elimina}</div>}
      </li>
    );
  }
  return (
    <li className="px-4 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">{testa}</div>
        {offerta && diff !== null && (
          <p className="flex min-h-11 shrink-0 items-center gap-1 text-sm font-semibold whitespace-nowrap">
            <Importo x={diff} /> <span className="font-medium text-text">al mese</span>
          </p>
        )}
        {incompleta && <p className="flex min-h-11 shrink-0 items-center text-sm text-muted-foreground">—</p>}
      </div>
      <dl className="mt-1 space-y-2 text-sm sm:grid sm:grid-cols-4 sm:gap-x-4 sm:space-y-0">
        <Voce titolo={COL_PCV}><span className="tabular-nums"><Pcv b={b} /></span></Voce>
        {b.dettaglio === "pronto" && (
          <>
            <Voce titolo={COL_SPREAD}><span className="tabular-nums"><Spread b={b} /></span></Voce>
            {/* conto non fatto: la voce non c'è (un'etichetta senza valore si legge come un dato perso); «—» = assente */}
            {b.ricarico_stato !== "non_calcolabile" && (
              <Voce titolo={COL_RICARICO} larga><Ricarico b={b} /></Voce>
            )}
            <Voce titolo={COL_ALTRI}><Altri b={b} /></Voce>
          </>
        )}
        {b.dettaglio === "in_corso" && (
          <>
            <div aria-busy="true" className="col-span-full text-sm italic text-muted-foreground">
              <span className="animate-pulse motion-reduce:animate-none">{IN_ANALISI}</span>
            </div>
          </>
        )}
      </dl>
      {b.dettaglio === "non_leggibile" && <p className="mt-2 text-sm text-muted-foreground">{DETTAGLIO_NON_LEGGIBILE}</p>}
      {(nota || pagina) && (
        <div className="mt-2 rounded-lg bg-surface p-3 text-sm text-text">
          {nota && <p><span className="font-medium">{nota[0]}</span> {nota[1]}</p>}
          {pagina && (
            <button
              type="button"
              onClick={() => pagina(b.id)}
              className={`${nota ? "mt-2 " : ""}inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-border bg-card text-sm font-medium text-text cursor-pointer hover:bg-surface transition-colors ${FOCUS}`}
            >
              <Plus size={16} aria-hidden="true" /> {AGGIUNGI_PAGINA}
            </button>
          )}
        </div>
      )}
      {/* unica fornitura: l'offerta è quella del condominio, si cambia dalla scheda del condominio (23/10 sera) */}
      {/* copia per l'amministratore: solo la fornitura con un'offerta sua la dice, senza «Cambia solo questa» */}
      {offerta && solaLettura && k.propria && (
        <div className="mt-2">
          <p className="text-xs text-muted-foreground">{SOLO_QUESTA_FORNITURA}</p>
          <p className="text-sm font-semibold text-text break-words"><OffertaBreve offerta={offerta} /></p>
        </div>
      )}
      {menu && <div className="mt-3">{menu}</div>}
      {elimina && <div className="mt-1">{elimina}</div>}
    </li>
  );
}

/** Una bolletta gas sul telefono: Mese, Fornitura, PCV €/mese e Altri addebiti; nessuna differenza né offerta. */
function SchedaGas({ b, k, elimina }: { b: BollettaVista; k: ContatoreGas; elimina: ReactNode }) {
  const avviso = avvisoGas(b);
  return (
    <li className="px-4 py-2">
      <p className="flex flex-col text-sm sm:flex-row sm:items-center sm:gap-x-1.5">
        <MeseLink b={b} className="self-start" />
        {k.fornitura && <span className="min-w-0 break-words text-text"><span aria-hidden="true" className="hidden text-muted-foreground sm:inline">·{" "}</span>{k.fornitura}</span>}
      </p>
      <dl className="mt-1 space-y-2 text-sm sm:grid sm:grid-cols-4 sm:gap-x-4 sm:space-y-0">
        <Voce titolo={COL_PCV}><span className="tabular-nums"><Pcv b={b} /></span></Voce>
        {/* analisi non fatta: nessuna riga «Altri addebiti» (un «—» direbbe «niente da segnalare»), l'avviso sotto dice perché */}
        {b.dettaglio === "pronto" && avviso === null && <Voce titolo={COL_ALTRI}><Altri b={b} /></Voce>}
        {b.dettaglio === "in_corso" && (
          <div aria-busy="true" className="col-span-full text-sm italic text-muted-foreground">
            <span className="animate-pulse motion-reduce:animate-none">{IN_ANALISI}</span>
          </div>
        )}
      </dl>
      {b.dettaglio === "non_leggibile" && <p className="mt-2 text-sm text-muted-foreground">{DETTAGLIO_NON_LEGGIBILE}</p>}
      {avviso && <p className="mt-2 text-sm text-muted-foreground">{avviso}</p>}
      {elimina && <div className="mt-1">{elimina}</div>}
    </li>
  );
}

/** Un condominio sotto i 1024 px (telefono, Fold, tablet verticale): intestazione e offerta, i numeri delle prime tre
 *  bollette in vista, le altre e gli addebiti da aprire. */
export function SchedaCondominio({
  condominio: c, offerte, onCambia, onPagina, className = "", occupato, onScegliContatore, onElimina, avvisoRiga,
}: Props) {
  const offerta = offertaDel(c, offerte);
  const { solaLettura } = useModo();
  const bollette = c.contatori.flatMap((k) => k.bollette.map((b) => ({ b, k })));
  // «Elimina» la prima volta che una bolletta compare (una bolletta di più forniture sta in più schede, luce e gas)
  const viste = new Set<string>();
  const elimina = (b: BollettaVista) => {
    if (solaLettura || viste.has(b.id)) return null;
    viste.add(b.id);
    return <EliminaBolletta b={b} onElimina={onElimina} />;
  };
  // i menù una volta per fornitura, sotto la sua ultima bolletta letta. Con una fornitura sola e nessuna offerta sua no:
  // l'offerta è quella del condominio, che sta subito sopra col suo tasto (23/10 sera; sul telefono i due si toccavano)
  const menuDi = (k: Contatore, b: BollettaVista): ReactNode => {
    const offertaK = offertaDelContatore(k, c, offerte);
    if (solaLettura || offertaK === null || (c.contatori.length === 1 && !k.propria)) return null;
    if (k.bollette.filter((x) => x.stato !== "non_letta").at(-1)?.id !== b.id) return null;
    return (
      <div role="group" aria-label={k.fornitura}>
        <p className="mb-2 text-xs font-medium text-muted-foreground">{k.propria ? SOLO_QUESTA_FORNITURA : OFFERTA_CONDOMINIO}</p>
        <SceltaOfferta
          offerte={offerte}
          valore={offertaK}
          onScegli={(id) => onScegliContatore(c, k, id)}
          contesto="contatore"
          onTorna={k.propria ? () => onScegliContatore(c, k, null) : undefined}
          disabilitato={occupato}
          avviso={avvisoRiga(k)}
        />
      </div>
    );
  };
  const esiti = esitiConFrase(c.esiti);
  // col gas (impostazione D): il titolo, poi la riga «Luce · …» con l'offerta e le schede luce, poi «Gas · …» e le schede gas
  const gas = c.contatori_gas ?? [];
  const conGas = gas.length > 0;
  const soloGas = c.contatori.length === 0;
  const esitiGas = esitiGasVisibili(esitiGasDi(c));
  const nEsiti = esiti.length + esitiGas.length;
  const voce = ({ b, k }: { b: BollettaVista; k: Contatore }) => (
    <SchedaBolletta key={`${k.chiave}-${b.id}`} b={b} k={k} offerta={offertaDelContatore(k, c, offerte)}
      onPagina={onPagina} menu={menuDi(k, b)} elimina={elimina(b)} />
  );
  return (
    <section className={`overflow-hidden rounded-xl border border-border bg-card ${className}`}>
      {conGas ? (
        <>
          <div className="p-4"><Intestazione c={c} soloNome /></div>
          {!soloGas && (
            <div className="space-y-3 border-t border-border p-4">
              <EtichettaTipo tipo="Luce" venditore={c.venditore} n={c.n_forniture} />
              {!(solaLettura && offerta === null) && (
                <div className="space-y-2">
                  <RiquadroOfferta offerta={offerta} mese={offerta ? c.mese : null} onCambia={onCambia} />
                  <NoteRisultato c={c} offerta={offerta} />
                </div>
              )}
            </div>
          )}
        </>
      ) : (
      <div className="space-y-3 p-4 sm:flex sm:items-start sm:justify-between sm:gap-4 sm:space-y-0">
        <Intestazione c={c} />
        {/* copia per l'amministratore senza offerta: niente riquadro (e niente spazio vuoto sotto il nome) */}
        {!(solaLettura && offerta === null) && (
          <div className="space-y-2 sm:w-[280px] sm:shrink-0">
            <RiquadroOfferta offerta={offerta} mese={offerta ? c.mese : null} onCambia={onCambia} />
            <NoteRisultato c={c} offerta={offerta} />
          </div>
        )}
      </div>
      )}
      <ul className="divide-y divide-border border-t border-border">
        {bollette.slice(0, IN_VISTA).map(voce)}
      </ul>
      {bollette.length > IN_VISTA && (
        <DaAprire titolo={leBollette(bollette.length)}>
          <ul className="divide-y divide-border border-t border-border">{bollette.slice(IN_VISTA).map(voce)}</ul>
        </DaAprire>
      )}
      {conGas && (
        <div className="border-t border-border">
          <div className="px-4 pt-3 pb-1"><EtichettaTipo tipo="Gas" venditore={c.venditore_gas ?? null} n={gas.length} /></div>
          <ul className="divide-y divide-border">
            {gas.flatMap((k) => k.bollette.map((b) => <SchedaGas key={b.id} b={b} k={k} elimina={elimina(b)} />))}
          </ul>
        </div>
      )}
      {nEsiti > 0 && (
        <DaAprire titolo={addebitiInPiu(nEsiti)}>
          {esiti.length > 0 && <Esiti esiti={esiti} className="px-4 pb-4 sm:grid-cols-2" />}
          {esitiGas.length > 0 && <Esiti gas esiti={esitiGas} className="px-4 pb-4 sm:grid-cols-2" />}
        </DaAprire>
      )}
    </section>
  );
}
